export interface Vital {
  _id: string;
  memberId: string;
  vitals: Array<{
    name: string;
    value: string;
    unit?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export enum VitalName {
  HEIGHT = "height",
  WEIGHT = "weight",
  STERILIZATION = "sterilization",
}

export enum UnitType {
  KG = "kg",
  FT_IN = "ft/in",
  CM = "cm",
}

export interface VitalDto {
  vitals: Array<{
    name: string;
    value: string;
    unit?: string;
  }>;
}

export type SterilizationStatus = "yes" | "no" | "unsure";

export const sterilizationOptions: {
  value: SterilizationStatus;
  label: string;
}[] = [
  { value: "yes", label: "Yes, my pet is sterilized" },
  { value: "no", label: "No, not sterilized yet" },
  { value: "unsure", label: "I'm not sure" },
];

// Phase 48 — Vitals Tracking types (Human health)
// Transforms API DTOs (_id → id) for consistent client-side conventions

/**
 * VitalsParameter Enum
 * Three vital parameters supported by the vitals tracking system
 */
export enum VitalsParameter {
  BP = "BP",
  GLUCOSE = "GLUCOSE",
  WEIGHT = "WEIGHT",
  HEIGHT = "HEIGHT",
}

/**
 * VitalsSwitchMember
 * Member shape used by AddReadingModal's member-switch control. `name` is the
 * resolved display label for the switcher trigger; the remaining fields mirror
 * MemberPickerDrawer's expected shape (avatar/initials row) for the drawer list.
 */
export interface VitalsSwitchMember {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  nickname?: string;
  avatar?: string;
}

/**
 * BPReadingPayload
 * Blood pressure reading with systolic, diastolic, and optional pulse
 */
export interface BPReadingPayload {
  systolic: number;
  diastolic: number;
  pulse?: number;
}

/**
 * GlucoseReadingPayload
 * Blood glucose reading with value and optional category hint
 */
export interface GlucoseReadingPayload {
  value: number;
  category?: string;
}

/**
 * WeightReadingPayload
 * Body weight reading with optional height for BMI calculation
 */
export interface WeightReadingPayload {
  value: number;
  height?: number;
}

/**
 * RecordReadingDto
 * Request DTO for recording a new vital reading
 */
export interface RecordReadingDto {
  parameter: VitalsParameter;
  values: Record<string, any>;
  context?: string;
  note?: string;
  recordedAt?: Date;
  source?: string;
}

/**
 * UpdateReadingDto
 * Request DTO for updating an existing vital reading
 * Editable fields: values, context, note, recordedAt
 */
export interface UpdateReadingDto {
  values?: Record<string, any>;
  context?: string;
  note?: string;
  recordedAt?: Date;
}

/**
 * VitalsReading
 * Full vital reading record (transformed from API with _id → id)
 */
export interface VitalsReading {
  id: string;
  memberId: string;
  parameter: VitalsParameter;
  values: {
    systolic?: number;
    diastolic?: number;
    pulse?: number;
    value?: number;
    category?: string;
    height?: number;
    bmi?: number;
  };
  recordedAt: Date;
  context?: string;
  note?: string;
  createdAt: Date;
  updatedAt?: Date;
}

/**
 * VitalsRange
 * Numeric range for a vital parameter (min/max values)
 * Used for BP systolic/diastolic and Glucose categories
 */
export interface VitalsRange {
  systolic?: [number, number];
  diastolic?: [number, number];
  fasting?: [number, number];
  postPrandial?: [number, number];
  random?: [number, number];
  beforeBed?: [number, number];
  height?: number; // stored in WEIGHT config; used to compute BMI client-side
}

/**
 * TrackingConfig
 * Member's vitals tracking configuration
 */
export interface TrackingConfig {
  id?: string;
  memberId: string;
  enabledParameters: VitalsParameter[];
  ranges: Partial<Record<VitalsParameter, VitalsRange>>;
  latestReadings?: Partial<Record<VitalsParameter, VitalsReading>>;
  confirmations?: unknown[];
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * UpdateRangeDto
 * Request DTO for updating vitals ranges
 */
export type UpdateRangeDto = Partial<Record<VitalsParameter, VitalsRange>>;

/**
 * ReadingConfirmation
 * Confirmation of a recorded initial reading with range snapshot
 */
export interface ReadingConfirmation {
  parameter: VitalsParameter;
  recordedAt: Date;
  values: Record<string, any>;
  recordedRange: Record<string, any>;
  recordingId: string;
  context?: string;
  note?: string;
}

/**
 * VitalsTrackingDto
 * Full vitals tracking state (enabled parameters, confirmations, ranges)
 * Represents the complete tracking configuration and recent readings
 */
export interface VitalsTrackingDto {
  enabledParameters: VitalsParameter[];
  confirmations: ReadingConfirmation[];
  ranges?: Record<string, any>;
}

// Backward compatibility aliases for Phase 46 code
export interface IEnableVitalsDto {
  enabledParameters: VitalsParameter[];
  initialReadings?: Record<string, any>;
}

export interface IDisableVitalsDto {
  parametersToDisable: VitalsParameter[];
}

export interface IReadingConfirmation {
  parameter: VitalsParameter;
  recordedAt: string;
  values: Record<string, any>;
  recordedRange: Record<string, any>;
  recordingId: string;
}

export interface IVitalsTrackingDto {
  enabledParameters: VitalsParameter[];
  confirmations: IReadingConfirmation[];
  ranges: Record<string, any>;
}

export interface IVitalsConfig {
  enabledParameters: VitalsParameter[];
  ranges: Record<string, any>;
}
